import { useEffect } from 'react';
import { useLocation, useRouteError } from 'react-router';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import { ERROR_PAGE_SUBTITLES, ERROR_PAGE_TITLES } from 'constants/error';
import { getErrorPageType, trackErrorPageViewed } from 'helpers/errorPageAnalytics';

import { Title, Text, Center, Image, Stack } from '@mantine/core';

import HalftoneBell from 'assets/images/halftone-bell.png';
import HalftoneIceCream from 'assets/images/icecream-fallen.png';

import ErrorButton from '../ErrorButton/ErrorButton';

export default function ErrorPage({
  title,
  subtitle,
  buttonCta,
  isFullScreen,
  isUnauthorized,
  error,
  noHeadquarterContext,
}) {
  const halftoneImage = isUnauthorized ? (
    <Image mb="lg" w={{ sm: 114, md: 170 }} src={HalftoneBell} />
  ) : (
    <Image mb="lg" w={{ sm: 200, md: 300 }} src={HalftoneIceCream} />
  );
  // Height depends if the error page takes up the full screen or resides in the auth layout
  const pageHeight = isFullScreen ? '75vh' : '100%';
  const routeError = useRouteError();
  const { pathname } = useLocation();
  const errorType = getErrorPageType({ error: routeError || error, isUnauthorized, title });

  useEffect(() => {
    if (routeError || error) {
      Sentry.captureException(routeError || error);
    }
  }, []);

  // one instance can stay mounted across routes, so re-log when the page changes
  useEffect(() => {
    trackErrorPageViewed({ errorType, pathname });
  }, [errorType, pathname]);

  function getTitle() {
    if (isUnauthorized) return ERROR_PAGE_TITLES.unauthorized;
    if (title) return title;
    return ERROR_PAGE_TITLES.general;
  }

  function getSubtitle() {
    if (isUnauthorized) return ERROR_PAGE_SUBTITLES.unauthorized;
    if (subtitle) return subtitle;
    return ERROR_PAGE_SUBTITLES.generalWithoutLink;
  }

  return (
    <Center style={{ flexGrow: 1, height: pageHeight }}>
      <Stack gap="md">
        <Center>{halftoneImage}</Center>
        <Title order={1} ta="center" tt="uppercase">
          {getTitle()}
        </Title>
        <Text w={{ sm: 'auto', md: 500 }} variant="body-lg" ta="center">
          {getSubtitle()}
        </Text>
        {buttonCta && (
          <Center>
            <ErrorButton label={buttonCta} noHeadquarterContext={noHeadquarterContext} />
          </Center>
        )}
      </Stack>
    </Center>
  );
}

ErrorPage.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  buttonCta: PropTypes.string,
  isFullScreen: PropTypes.bool,
  isUnauthorized: PropTypes.bool,
  error: PropTypes.shape({}),
  noHeadquarterContext: PropTypes.bool,
};
